/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { useCallback, useMemo } from 'react';

import { type BaseFeatureFlags } from '../../launch-darkly.types';
import { useAsyncStorageOverrides } from '../useAsyncStorageOverrides';
import { useQueryParamsOverridesEffect } from '../useQueryParamsOverridesEffect';

// ─────────────────────────────────────────────────────────────────────────────

export const useFeatureFlagOverrides = <FeatureFlags extends BaseFeatureFlags>(
  params: Params<FeatureFlags>,
) => {
  const { localOverrides } = params;

  const asyncStorageOverrides = useAsyncStorageOverrides<FeatureFlags>();

  // ─── Derived Data ────────────────────────────────────────────────────

  const allOverrides = useMemo<FeatureFlags>(
    () => ({ ...asyncStorageOverrides.overrides, ...localOverrides }),
    [asyncStorageOverrides, localOverrides],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const assignFeatureFlagOverrides = useCallback(
    (flags: Partial<FeatureFlags>) => {
      asyncStorageOverrides.assignFeatureFlagOverrides(flags);
    },
    [asyncStorageOverrides],
  );

  const overrideFeatureFlag = useCallback(
    (key: keyof FeatureFlags, value: unknown) => {
      const entry = { [key]: value } as Partial<FeatureFlags>;

      assignFeatureFlagOverrides(entry);
    },
    [assignFeatureFlagOverrides],
  );

  const clearFeatureFlagOverrides = useCallback(() => {
    asyncStorageOverrides.clearFeatureFlagOverrides();
  }, [asyncStorageOverrides]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useQueryParamsOverridesEffect<FeatureFlags>({
    onQueryParamsDetected: asyncStorageOverrides.assignFeatureFlagOverrides,
  });

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      allOverrides,
      overrideFeatureFlag,
      clearFeatureFlagOverrides,
    }),
    [allOverrides, overrideFeatureFlag, clearFeatureFlagOverrides],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params<FeatureFlags extends BaseFeatureFlags> = {
  localOverrides: Partial<FeatureFlags> | undefined;
};
